import React from 'react';
import PropTypes from 'prop-types';
import webAuth from '../../auth';
import isWebview from 'is-webview';
import { emailIsValid } from '../../utils';
import { Intent, Icon, Tab, Tabs } from '@blueprintjs/core';
import facebook from './facebook.svg';
import google from './google.svg';
import { CONTACT_EMAIL } from '../../constants';
import { capitalize } from '../../utils';
import './index.css';

const EMAIL_ERROR = "Invalid email.";
const GENERAL_ERROR = `Error. Contact ${CONTACT_EMAIL} for help.`;
const PYTHON_INTERPOLATIONS = /%s|%d|%r|%f/;
const HELPER_STYLE = {margin: 5};
const SUB_HELPERS_STYLE = {marginLeft: 10};
const WEAK_PWD = "Your password is weak. It must have:";
const DB_CONNECTION = 'Username-Password-Authentication';

// Error codes
const USER_EXISTS = "user_exists";
const INVALID_PWD = "invalid_password";

export const SuccessHelper = (props) =>
  <div className="pt-form-helper-text flex-center-row" style={HELPER_STYLE}>
    <Icon style={{marginRight: 5}} icon="tick" intent={Intent.SUCCESS}/>
    <div className="success">{props.text || 'Success!'}</div>
  </div>;

export const ErrorHelper = (props) =>
  <div className="pt-form-helper-text flex-center-row" style={HELPER_STYLE}>
    <Icon style={{marginRight: 5}} icon="cross" intent={Intent.DANGER}/>
    <div className="danger">{props.text || GENERAL_ERROR }</div>
  </div>;

function beautify(string) {
  return capitalize(string);
}

function pythonStringInterpolation(pattern, items) {
  let result = pattern;
  items.map(item => result = result.replace(PYTHON_INTERPOLATIONS, item));
  return result;
}

function createHelper(rule, key) {
  let helper;
  let text;
  if (rule.message && rule.format) {
    text = pythonStringInterpolation(rule.message, rule.format);
  } else {
    text = rule.message;
  }
  text = beautify(text);

  let subHelpers = null;
  if (rule.items) {
    subHelpers = (
      <div style={SUB_HELPERS_STYLE}>
        {createHelpers(rule.items)}
      </div>
    );
  }

  if (rule.verified) {
    helper = <SuccessHelper text={text}/>;
  } else {
    helper = <ErrorHelper text={text}/>;
  }

  return (
    <div key={key}>
      {helper}
      {subHelpers}
    </div>
  );
}


/**
 * @param rules {Object} Example rules object:
 [
 {
   "message":"At least %d characters in length",
   "format":[8],
   "verified":false
 },
 {
   "message":"Contain at least %d of the following %d types of characters:",
   "format":[3,4],
   "items":[
     {"message":"lower case letters (a-z)","verified":true},
     {"message":"upper case letters (A-Z)","verified":false},
     {"message":"numbers (such as 0-9)","verified":false},
     {"message":"special characters (such as !@#$%^&*)","verified":false}
   ],
   "verified":false
 }
 ]
 * @returns {XML}
 */
function createHelpers(rules) {
  const helpers = [];
  rules.forEach(rule => {
    const key = rules.indexOf(rule);
    helpers.push(createHelper(rule, key))
  });
  return helpers;
}

/**
 * More on Auth0 password strength errors:
 * - https://auth0.com/docs/connections/database/password-strength
 */
function parseError(error) {
  if (error.description) { // At signup
    if (typeof error.description === 'string') {
      return <ErrorHelper text={capitalize(error.description)}/>;
    } else {
      const rules = error.description.rules;
      if (rules) {
        let pwdMessage = null;
        if (error.code === INVALID_PWD) {
          pwdMessage = <ErrorHelper text={WEAK_PWD}/>;
        }
        return <div>{pwdMessage}{createHelpers(rules)}</div>;
      } else {
        return <ErrorHelper text={GENERAL_ERROR}/>;
      }
    }
  } else if (error.error_description) { // At login
    return <ErrorHelper text={error.error_description}/>;
  } else {
    return <ErrorHelper text={GENERAL_ERROR}/>;
  }
}


/**
 * Auth0 API references:
 * - https://auth0.github.io/auth0.js/global.html (full doc)
 * - https://auth0.com/docs/libraries/auth0js/v9
 */
class Lock extends React.Component {

  state = {
    passwordlessEmail: '',
    passwordlessIntent: Intent.NONE,
    passwordlessMessage: '',
    passwordEmail: '',
    password: '',
    passwordIntent: Intent.NONE,
    passwordMessage: '',
    showPasswordReset: false,
    loginMode: true
  };

  onFacebookClick = () => {
    webAuth.authorize({
      connection: 'facebook'
    });
  };

  onGoogleClick = () => {
    webAuth.authorize({
      connection: 'google-oauth2'
    });
  };

  resetIntentsAndHelpers = () => {
    this.setState({
      passwordlessIntent: Intent.NONE,
      passwordIntent: Intent.NONE,
      passwordlessMessage: '',
      passwordMessage: ''
    });
  };

  onPasswordlessSubmit = (codeOrLink) => {
    return e => {
      if (e) {
        e.preventDefault();
      }

      this.resetIntentsAndHelpers();

      if (emailIsValid(this.state.passwordlessEmail)) {
        webAuth.passwordlessStart({
            connection: 'email',
            send: codeOrLink, // can be either 'link' or 'code'
            email: this.state.passwordlessEmail,
            // user_metadata: {}
          }, function (err) {
            if (err) {
              this.setState({
                passwordlessMessage: parseError(err),
                passwordlessIntent: Intent.DANGER
              })
            } else {
              this.setState({
                passwordlessMessage: <SuccessHelper
                  text={`We emailed you a login ${codeOrLink}.`}/>,
                passwordlessIntent: Intent.NONE
              })
            }
          }.bind(this)
        );
      } else {
        this.setState({
          passwordlessMessage: <ErrorHelper text={EMAIL_ERROR}/>,
          passwordlessIntent: Intent.DANGER,
        })
      }
    };
  };

  login = () => {
    webAuth.login({
      realm: DB_CONNECTION,
      email: this.state.passwordEmail,
      password: this.state.password
    }, function (err) {
      if (err) {
        this.setState({
          passwordMessage: parseError(err),
          passwordIntent: Intent.DANGER
        })
      }
    }.bind(this));
  };

  onPasswordSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }

    this.resetIntentsAndHelpers();
    if (emailIsValid(this.state.passwordEmail)) {

      if (this.state.loginMode) {
        this.login();
      } else {
        webAuth.signup({
          connection: DB_CONNECTION,
          email: this.state.passwordEmail,
          password: this.state.password,
        }, function (err) {
          if (err) {
            this.setState({
              passwordMessage: parseError(err),
              passwordIntent: Intent.DANGER
            })
          } else {
            this.login();
          }
        }.bind(this));
      }
    } else {
      this.setState({
        passwordMessage: <ErrorHelper text={EMAIL_ERROR}/>,
        passwordIntent: Intent.DANGER,
      })
    }
  };

  onForgotPasswordSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }
    this.resetIntentsAndHelpers();
    if (emailIsValid(this.state.passwordEmail)) {
      webAuth.changePassword({
        connection: DB_CONNECTION,
        email: this.state.passwordEmail
      }, function (err, resp) {
        if (err) {
          this.setState({
            passwordMessage: parseError(err),
            passwordIntent: Intent.DANGER
          })
        } else {
          this.setState({
            passwordMessage: <SuccessHelper text={resp}/>,
            passwordIntent: Intent.SUCCESS
          })
        }
      }.bind(this));
    } else {
      this.setState({
        passwordMessage: <ErrorHelper text={EMAIL_ERROR}/>,
        passwordIntent: Intent.DANGER,
      })
    }
  };

  toggleForgotPassword = () => {
    this.resetIntentsAndHelpers();
    this.setState({showPasswordReset: !this.state.showPasswordReset})
  };

  handleTabSelection = (tabId) => {
    this.resetIntentsAndHelpers();
    this.setState({loginMode: tabId === "login"})
  };

  render() {

    const loginOrSignUp = this.state.loginMode ? "Log In" : "Sign Up";
    const facebookButton = (
      <button
        className="SocialLogin-button pt-button pt-intent-primary pt-fill"
        onClick={this.onFacebookClick}>
        <div className="SocialLogin-button-content">
          <div className="SocialLogin-icon">
            <img src={facebook} width={16} height={16} alt="facebook"/>
          </div>
          <div className="SocialLogin-text">
            {loginOrSignUp + " with Facebook"}
          </div>
        </div>
      </button>
    );

    // Google auth will not work if the user agent is a webView
    const googleButton = isWebview(navigator.userAgent) ? null : (
        <button
          className="SocialLogin-button pt-button pt-intent-primary pt-fill"
          onClick={this.onGoogleClick}>
          <div className="SocialLogin-button-content">
            <div className="SocialLogin-icon">
              <img src={google} width={16} height={16} alt="google"/>
            </div>
            <div className="SocialLogin-text">
              {loginOrSignUp + " with Google"}
            </div>
          </div>
        </button>
      );

    const passwordlessSubmitButton = (
      <button
        className="pt-button pt-minimal pt-intent-primary pt-icon-arrow-right"
        onClick={this.onPasswordlessSubmit(this.props.passwordless)}
      />
    );

    const passwordlessInputClass = (
      `pt-input-group pt-intent-${this.state.passwordlessIntent}`
    );

    const passwordlessForm = (
      <form className='pt-form-group pt-fill full-width'
            onSubmit={this.onPasswordlessSubmit}>
        <div className="pt-form-content">
          <div className={passwordlessInputClass}>
            <Icon icon="envelope"/>
            <input type="text"
                   className="pt-input"
                   autoComplete="email"
                   placeholder="Email"
                   onChange={event => this.setState({
                     passwordlessEmail: event.target.value
                   })}
            />
            {passwordlessSubmitButton}
          </div>
          {this.state.passwordlessMessage}
        </div>
      </form>
    );

    const passwordInputClass = (
      `pt-input-group pt-intent-${this.state.passwordIntent}`
    );

    const passwordForm = (
      <form className='pt-form-group pt-fill full-width'
            onSubmit={this.onPasswordSubmit}>
        <div className="pt-form-content">
          <div className="pt-control-group pt-vertical full-width">
            <div className={passwordInputClass}>
              <Icon icon="envelope"/>
              <input type="text"
                     name="username"
                     className="pt-input"
                     autoComplete="email"
                     placeholder="Email"
                     onChange={event => this.setState({
                       passwordEmail: event.target.value
                     })}
              />
            </div>
            <div className={passwordInputClass}>
              <Icon icon="lock"/>
              <input type="password"
                     name="password"
                     className="pt-input"
                     placeholder="Password"
                     onChange={event => this.setState({
                       password: event.target.value
                     })}
              />
            </div>
            <button className="pt-button pt-intent-primary">
              {loginOrSignUp}
            </button>
          </div>
          {this.state.passwordMessage}
        </div>
        {this.state.loginMode ? (
            <a
              role="button"
              className="Lock-forgot-password-link"
              onClick={this.toggleForgotPassword}
            >
              {"Forgot your password?"}
            </a>
          ) : null
        }
      </form>
    );

    const forgotPasswordForm = (
      <form className='pt-form-group pt-fill full-width'
            onSubmit={this.onForgotPasswordSubmit}>
        <div className="pt-form-content">
          <div className="pt-control-group pt-vertical full-width">
            <div className={passwordInputClass}>
              <Icon icon="envelope"/>
              <input type="text"
                     name="username"
                     className="pt-input"
                     autoComplete="email"
                     placeholder="email"
                     onChange={event => this.setState({
                       passwordEmail: event.target.value
                     })}
              />
            </div>
            <button className="pt-button pt-intent-primary">
              {"Send Password Reset Link"}
            </button>
          </div>
          {this.state.passwordMessage}
        </div>
        <a
          role="button"
          className="Lock-forgot-password-link"
          onClick={this.toggleForgotPassword}
        >
          <Icon icon="arrow-left"/>
          <div style={{marginLeft: 5}}>Back</div>
        </a>
      </form>
    );

    const passwordless = (
      <div className="full-width flex-center-column">
        Or get a login {this.props.passwordless} to your inbox
        <div className="horizontal-divider"/>
        {passwordlessForm}
      </div>
    );

    return (
      <div className="Lock">
        <div className="horizontal-divider"/>
        <Tabs id="login-or-sign-up"
              onChange={this.handleTabSelection}
              selectedTabId={this.state.loginMode ? 'login' : 'signup'}
        >
          <Tab id="login" title="Log In" panel={null}/>
          <Tab id="signup" title="Sign Up" panel={null}/>
        </Tabs>
        {googleButton}
        <div className="horizontal-divider"/>
        {/*{facebookButton}*/}
        {/*<div className="horizontal-divider"/>*/}
        {this.state.showPasswordReset ? forgotPasswordForm : passwordForm}
        <div className="horizontal-divider"/>
        {this.props.passwordless ? passwordless : null}
      </div>
    );
  }
}

Lock.propTypes = {
  passwordless: PropTypes.oneOf(['code', 'link'])
};

export default Lock;



// WEBPACK FOOTER //
// ./src/components/Lock/index.js